import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchCompaniesByPlatformFeatureId } from '@/api/(admin)/platform-features';
import type { FeatureCompaniesQueryInput, FeatureCompanyRow } from '../-types';
import {
  buildFeatureCompaniesQs,
  toFeatureCompaniesParams,
} from '../-utils/listParams';

/**
 * The companies holding one feature.
 *
 * Keyed on the *converted* params rather than the raw input, so the loader's
 * URL-shaped values and `useTable`'s state-shaped values land on the same cache
 * entry — otherwise the prefetch warms a key nothing reads and the page still
 * flashes a spinner.
 */
export function featureCompaniesQueryOptions(
  input: FeatureCompaniesQueryInput,
) {
  const params = toFeatureCompaniesParams(input);
  return queryOptions({
    queryKey: [
      'admin',
      'platform-features',
      'companies',
      input.featureId,
      params,
    ] as const,
    queryFn: () =>
      fetchCompaniesByPlatformFeatureId(
        input.featureId,
        buildFeatureCompaniesQs(params),
      ),
    enabled: Boolean(input.featureId),
  });
}

export function useFeatureCompanies(input: FeatureCompaniesQueryInput) {
  const query = useQuery(featureCompaniesQueryOptions(input));

  const rows: FeatureCompanyRow[] = query.data?.data ?? [];

  return {
    ...query,
    rows,
    totalCount: query.data?.metadata?.pagination?.total ?? 0,
  };
}
